import { useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { type Organization, useOrganizations } from '@/hooks/useOrganizations';
import { ExperienceTypes, OrganizationType, Role } from '@/types/gql.generated';
import { Box, Button, Flex, Icon, Text, useDisclosure } from '@/ui';
import {
  ShareWithWorkspaceOptions,
  useMoveToWorkspace,
  WorkspacePicker,
} from '../../MoveToWorkspace';
import { useShareModalStore } from '../store';

export const MoveToWorkspaceLink = () => {
  const experience = useEffectiveExperience();
  const { sharedOrganizations } = useOrganizations();
  const { close: closeShareModal } = useShareModalStore();
  const { isOpen, onToggle } = useDisclosure();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [destination, setDestination] = useState<Organization | null>(null);
  const [shareWithWorkspace, setShareWithWorkspace] = useState(true);
  const [shareRole, setShareRole] = useState<Role>(Role.Viewer);

  const { move, isPending } = useMoveToWorkspace(experience.id, {
    onMoved: () => {
      setDestination(null);
      closeShareModal();
    },
    // The move unlocked General Access, which is where they can finish sharing,
    // so close the confirm but leave Share itself open.
    onSharingFailed: () => setDestination(null),
  });

  if (
    experience.type !== ExperienceTypes.Planner ||
    experience.role !== Role.Owner ||
    experience.org?.type !== OrganizationType.Personal ||
    // Nothing to offer: they belong to no shared workspace yet.
    sharedOrganizations.length === 0
  ) {
    return null;
  }

  // Resolved rather than stored so a membership disappearing underneath the
  // selection falls back to a real workspace instead of an empty control.
  const selected =
    sharedOrganizations.find((org) => org.id === selectedId) ??
    sharedOrganizations[0];

  return (
    <Flex direction="column">
      <Button
        alignSelf="flex-start"
        colorScheme="dark"
        fontSize="sm"
        fontWeight="normal"
        // A Button rather than a Link: no destination, and an anchor without
        // an href is not keyboard operable.
        type="button"
        variant="link"
        rightIcon={
          <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size={4} />
        }
        onClick={onToggle}
      >
        Move to a different workspace
      </Button>

      {isOpen && (
        <Flex direction="column" gap="3" mt="4">
          <Text color="customgray.mid" fontSize="sm">
            This planner is in your personal workspace. Move it to a shared
            workspace to share it with everyone there.
          </Text>

          <Flex align="center" gap="2">
            <WorkspacePicker
              destinations={sharedOrganizations}
              isDisabled={isPending}
              selected={selected}
              onChange={setSelectedId}
            />

            <Button
              flexShrink="0"
              isDisabled={isPending}
              onClick={() => setDestination(selected)}
            >
              Move
            </Button>
          </Flex>
        </Flex>
      )}

      {destination && (
        <ConfirmDialog
          confirmButtonText={`Yes, move to ${destination.name}`}
          isLoading={isPending}
          isOpen
          message={`This planner and its pages will move out of your personal workspace and into ${destination.name}. This can't be undone and you won't be able to move it back to your personal account.`}
          title={`Move this planner to ${destination.name}?`}
          onCancel={() => setDestination(null)}
          onConfirm={() => move(destination, { shareWithWorkspace, shareRole })}
        >
          <Box mt="6">
            <ShareWithWorkspaceOptions
              isDisabled={isPending}
              isShared={shareWithWorkspace}
              role={shareRole}
              workspaceName={destination.name}
              onRoleChange={setShareRole}
              onSharedChange={setShareWithWorkspace}
            />
          </Box>
        </ConfirmDialog>
      )}
    </Flex>
  );
};
